import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DataGrid, Column, FilterRow, Scrolling, SearchPanel, Lookup, Editing, Selection } from 'devextreme-react/data-grid';
import { Button, TextBox } from 'devextreme-react';
import DropDownBox from 'devextreme-react/drop-down-box';

import { formatMessage } from 'devextreme/localization';

import CustomStore from 'devextreme/data/custom_store';
import GlobalLoader from '../loader/GlobalLoader';

import ProcessingResult from '../../framework/ProcessingResult';

import { UserRolesHelper } from '../../logics/usermodule/UserRolesHelper';

import UserInvitationForm, { UserInvitationFormHandle } from '../user-invitation/UserInvitationForm';

import MessageForm, { MessageFormHandle, ErrorMessage, WarningMessage } from '../../components/message-form/MessageForm';

import UserApi from '../../api/UserApi';
import RoleGroupApi, { RoleGroupFilterMode } from '../../api/RoleGroupApi';

import UserListDto from '../../classes/dtos/accountmodule/UserListDto';
import RoleGroupDto from '../../classes/dtos/rolegroupmodule/RoleGroupDto';

import { useAuth } from '../../contexts/auth';


interface UserGridProps {
    EnterpriseMode: boolean;
    GlobalUser: boolean;
    WorkspaceUser: boolean;
}

const UserGrid: React.FC<UserGridProps> = ({ EnterpriseMode, GlobalUser, WorkspaceUser }) => {

    const ChangeUserRoleDone: number = 1;
    const ChangeUserRoleNoOtherOwner: number = 2;

    const userInvitationFormHandleRef = useRef<UserInvitationFormHandle>(null);

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const [users, setUsers] = useState<Array<UserListDto> | null>(null);
    const [roleGroups, setRoleGroups] = useState<Array<RoleGroupDto> | null>(null);

    const { sessionDetail } = useAuth();

    const [currentUserId] = useState<number>(useAuth().user?.UserId ?? 0);

    const [invitationAuthorized, setInvitationAuthorized] = useState<boolean>(false);

    const inviteButtonText = WorkspaceUser
        ? formatMessage("SettingsUserGrid_InviteUserWorkspace", sessionDetail?.WorkspaceName ?? "")
        : formatMessage(EnterpriseMode ? "SettingsUserGrid_InviteUserGlobal" : "SettingsUserGrid_InviteUser");
    
    function invited() {
        setUsers(null);
    }

    const refresh = useCallback(() => {
        if (!users) {
            UserApi.GetUserListAsync(WorkspaceUser).then((allUsers: ProcessingResult<UserListDto[]>) => {
                if (allUsers.IsFailed()) {
                    // TODO: Handle failure case (e.g., logout)
                } else {
                    let owner = allUsers.Result.find(e => e.Id === currentUserId && e.IsOwner)?.IsOwner ?? false;
                    setInvitationAuthorized(owner);
                    setUsers(allUsers.Result);
                }
            });
        }
    }, [users, currentUserId]);

    // UseEffect to refresh when component mounts
    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (EnterpriseMode && roleGroups === null) {
            const filterMode = WorkspaceUser ? RoleGroupFilterMode.Workspace : RoleGroupFilterMode.WithoutWorkspace;

            RoleGroupApi.GetRoleGroupAsync(filterMode).then(result => {
                if (result.IsSuccess()) {
                    setRoleGroups(result.Result);
                }
            });
        }
    }, [EnterpriseMode, WorkspaceUser, roleGroups]);

    function inviteUser(): void {
        if (userInvitationFormHandleRef.current !== null) {
            userInvitationFormHandleRef.current.Open();
        }
    };

    const customDataSource = new CustomStore({
        key: 'Id',
        load: () => {
            return users || [];
        },
        update: async (key: number, value) => {
            if (EnterpriseMode) {
                let result = await UserApi.ChangeUserRoleGroupsAsync(key, value.RoleGroupIds);
                if (result.IsFailed()) {
                    messageFormHandleRef.current?.Open(formatMessage("SettingsUserGrid_Error"), ErrorMessage);
                } else {
                    setUsers(null);
                }
                return;
            }
            let result = await UserApi.ChangeUserRoleAsync(key, value.UserRole);
            if (result.IsSuccess()) {
                if (result.Result === ChangeUserRoleDone) {
                    setUsers(null);
                }
                if (result.Result === ChangeUserRoleNoOtherOwner) {
                    messageFormHandleRef.current?.Open(formatMessage("SettingsUserGrid_NoOtherOwner"), WarningMessage);
                }
            } else {
                messageFormHandleRef.current?.Open(formatMessage("SettingsUserGrid_Error"), ErrorMessage);
            }
        }
    });

    const userRoles = UserRolesHelper.getUserRoles();

    if (!users) {
        return <GlobalLoader />
    }

    const GroupsInDropDown = (cellInfo: any) => {
        const [selectedKeys, setSelectedKeys] = useState<number[]>(cellInfo.value || []);
        const [isOpened, setIsOpened] = useState(false);

        const onSelectionChanged = (e: any) => {
            setSelectedKeys(e.selectedRowKeys);
            cellInfo.setValue(e.selectedRowKeys);
        };

        return (
            <DropDownBox
                value={selectedKeys}
                opened={isOpened}
                valueExpr="Id"
                deferRendering={false}
                placeholder="Select groups..."
                showClearButton={false}
                dataSource={roleGroups}
                onValueChanged={(e) => {
                    setSelectedKeys(e.value || []);
                    cellInfo.setValue(e.value || []);
                }}
                onOpenedChange={(opened) => setIsOpened(opened)}
                fieldRender={() => (
                    <TextBox>
                        <div style={{ marginLeft: '16px', flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px' }}>
                            {(roleGroups || [])
                                .filter(rg => (cellInfo.value ?? []).includes(rg.Id))
                                .map((rg, idx, arr) => (
                                    <span key={rg.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                                        {rg.Name}
                                    </span>
                                ))
                            }
                            {(!cellInfo.value || cellInfo.value.length === 0) && (
                                <span>No groups</span>
                            )}
                        </div>
                    </TextBox>
                )}
                contentRender={() => (
                    <DataGrid
                        dataSource={roleGroups}
                        selectedRowKeys={selectedKeys}
                        onSelectionChanged={onSelectionChanged}
                        hoverStateEnabled={true}
                        height={300}
                        keyExpr="Id"
                        showBorders={true}
                    >
                        <Selection mode="multiple" showCheckBoxesMode="always" />
                        <Column dataField="Name" caption="Name" />
                    </DataGrid>
                )}
            />
        );
    };

    const groupTagsCell = (cellData: any) => {
        const groupIds: number[] = cellData.value || [];
        const selected = (roleGroups ?? []).filter(rg => groupIds.includes(rg.Id));
        return (
            <div style={{ flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px' }}>
                {selected.length === 0 ? (
                    <span>No groups</span>
                ) : (
                    selected.map((rg, idx, arr) => (
                        <span key={rg.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                            {rg.Name}
                        </span>
                    ))
                )}
            </div>
        );
    };

    return (
        <div className={"block-card"}>
            <div className={"block-card__header"}>
                <h2>{"All your users"}</h2>
                <Button icon="plus" text={inviteButtonText} type="default" stylingMode="contained" onClick={inviteUser} disabled={!invitationAuthorized} />
            </div>
            <DataGrid
                id="dataGrid"
                dataSource={customDataSource}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={false}
                hoverStateEnabled={true}
                height='calc(100vh - 400px)'
            >
                <Scrolling mode="infinite" />
                <FilterRow visible={false} />
                <SearchPanel visible={false} />
                <Editing
                    mode="row"
                    allowUpdating={true}
                    allowAdding={false}
                    allowDeleting={false}
                />
                <Column caption="EMAIL" dataField="Email" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption="FIRSTNAME" dataField="FirstName" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption="LASTNAME" dataField="LastName" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption="ROLE" dataField="UserRole" dataType="number" allowSorting={false} allowEditing={true} visible={!EnterpriseMode}>
                    <Lookup
                        displayExpr="Name"
                        valueExpr="Id"
                        dataSource={userRoles}
                    />
                </Column>
                <Column caption="GROUPS" dataField="RoleGroupIds" allowSorting={false} allowEditing={true} visible={EnterpriseMode} editCellRender={GroupsInDropDown} cellRender={groupTagsCell} />
                <Column width={120} caption="STATUS" dataField="StatusLabel" dataType="string" allowSorting={false} allowEditing={false} />
            </DataGrid>
            <UserInvitationForm ref={userInvitationFormHandleRef} Invited={invited} WorkspaceUser={WorkspaceUser} />
            <MessageForm ref={messageFormHandleRef} />
        </div>
    );
};

export default UserGrid;
